import React, { useMemo, useEffect, useState } from 'react';
import styles from './CaseListItem.module.scss';
import { useAppContext } from '../../../../Services/AppContext';
import moment from 'moment';
import { Theme } from '../../../../Enums/Theme.enum';
import CaseForwarder from '../CaseForwarder/CaseForwarder';
import Button from '../../../../Components/Button/Button';
import CloseCaseButton from '../../../../Components/Case/CloseCaseButton';
import { joinClasses } from '../../../../Utils/className.util';
import CaseNoteButton from '../CaseNote/CaseNoteButton';
import useTranslation from '../../../../Hooks/useTranslation';
import Text from '../../../../Components/Base/Text/Text';
import { acceptCase } from '../../Services/case.service';
import OverlayLoading from '../../../../Components/OverlayLoading';
import UserAvatarImage from '../../../../Components/UserAvatar/UserAvatarImage';
import usePatientMeta from '../../../../Components/PatientProfile/Hooks/usePatientMeta';
import CaseCountdownBadge from '../CaseCountdownBadge/CaseCountdownBadge';
import { getMomentDate } from '../../../../Utils/dates.util';
import CaseNotePreview from '../CaseNote/CaseNotePreview';
import { CaseItemMedicalReport } from '../../Interfaces/Case.interface';
import { RequestPriority } from '../../../MonitoringCenter/Enums/RequestPriority.enum';
import usePatientIdentification from '../../../Patient/Hooks/usePatientIdentification';
import { Case, CaseTag, Meta, User } from '../../../../graphql/graphql';
import PatientPartnerBadge from '../../../Patient/Components/PatientPartnerBadge/PatientPartnerBadge';
import LinkButton from '../../../../Components/Button/LinkButton';
import CaseChatPreviewModal from '../../../CaseChat/Components/CaseChatPreviewModal';
import CasePermanentMedicationOptionsContextMenu from '../CasePermanentMedicationOptionsContextMenu/CasePermanentMedicationOptionsContextMenu';
import PatientProfileButton from '../../../Patient/Components/PatientProfileButton/PatientProfileButton';
import CaseTagBadge from '../CaseTag/CaseTagBadge';

const PRIORITY_COLORS = {
	[RequestPriority.NONE]: Theme.GREY,
	[RequestPriority.LOW]: Theme.GREEN,
	[RequestPriority.MEDIUM]: Theme.ORANGE,
	[RequestPriority.HIGH]: Theme.RED,
};

const PRIORITY_TRANSLATIONS = {
	[RequestPriority.NONE]: 'component.monitoringCenter.priority.none',
	[RequestPriority.LOW]: 'component.monitoringCenter.priority.low',
	[RequestPriority.MEDIUM]: 'component.monitoringCenter.priority.medium',
	[RequestPriority.HIGH]: 'component.monitoringCenter.priority.high',
};

export interface CaseListItemInterface {
	item: {
		id: string;
		code: string;
		priority: { value: RequestPriority };
		title: string;
		createdAt: string;
		createdBy: {
			id: string;
			isPatient: boolean;
		};
		solvedAt?: string;
		isAssigned?: boolean;
		isNew: boolean;
		isMyCase: boolean;
		isImmediate: any;
		isMeddi: boolean;
		patient: User;
		doctor?: {
			id: string;
			profile: {
				fullName: string;
			};
		};
		workplace?: {
			id: string;
			doctorDisplayName: string;
		};
		lastNote?: {
			id: string;
			text: string;
		};
		lastCaseEntry?: {
			time: string;
		};
		medicalReports?: CaseItemMedicalReport[];
		patientParent?: {
			id: string;
			profile: {
				fullName: string;
			};
		};
		totalUnreadCaseEntries?: number;
		child?: {
			id: string;
		};
		reassignedFrom: {
			id: string;
			workplace: {
				doctorDisplayName: string;
			};
			user: {
				profile: {
					fullName: string;
				};
			};
		};
		caseChatPreview?: boolean;
		event?: {
			id: string;
		};
		caseType?: {
			code: string;
		};
		medicationLasted?: { value: string; medicationId: string }[];
		tags?: CaseTag[];
	};
	showWorkplace?: boolean;
	showAssignedDoctor?: boolean;
	onRefresh?: () => void;
	doctorSurvey?: {
		show: boolean;
		wizardKey: string;
	};
}

const CaseListItem: React.FC<CaseListItemInterface> = ({
	item,
	showWorkplace = false,
	showAssignedDoctor = false,
	onRefresh,
}) => {
	const { workplaceId, userData, dateFormatMoment, socket } = useAppContext();
	const { identification } = usePatientIdentification(item.patient as User);
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [totalUnreadCaseEntries, setTotalUnreadCaseEntries] = useState(
		item?.totalUnreadCaseEntries?.toString(),
	);
	const [isLate, setIsLate] = useState(false);
	const [isChatPreviewOpen, setIsChatPreviewOpen] = useState(false);

	const {
		getPersonActingOnBehalfOfAPatient,
		hasPersonActingOnBehalfOfAPatient,
	} = usePatientMeta(item.patient?.id, item.patient?.meta as Meta[]);

	const handleConnectWithPatient = async (
		caseId: string,
		takesOverCase = false,
	) => {
		setIsLoading(true);
		try {
			const reponse = await acceptCase(
				caseId,
				userData.id,
				false,
				takesOverCase,
			);
			if (reponse?.status === 200 && caseId) {
				window.location.href = '/ordinace/detail/' + caseId;
			}
		} catch (e) {
			console.error(e);
		} finally {
			setIsLoading(false);
		}
	};

	const refreshUnreadCaseEntries = async (data: any) => {
		if (data?.case?.id === item.id) {
			setTotalUnreadCaseEntries(data?.totalUnreadCaseEntries);
		}
	};

	const handleReloadCaseList = () => {
		onRefresh?.();
	};

	useEffect(() => {
		socket.on('caseEntryCreated', refreshUnreadCaseEntries);
		return () => {
			socket.off('caseEntryCreated', refreshUnreadCaseEntries);
		};
	}, []);

	const isDisabled = !item.isNew && !item.isMyCase;

	const title: string = useMemo(() => {
		return item?.createdBy?.isPatient && item?.event?.id
			? t('component.caseListItem.thePatientOrderedHimself') + ': ' + item.title
			: item.title;
	}, [item]);

	if (!item.patient) {
		return null;
	}

	return (
		<>
			{isLoading && <OverlayLoading />}
			<div
				className={joinClasses([
					styles.item,
					isDisabled ? styles.disabled : undefined,
					isLate ? styles.isLate : undefined,
				])}>
				<div className={styles.patient}>
					<div className={styles.patientImageContainer}>
						{!!item?.isImmediate?.value && (
							<CaseCountdownBadge
								className={styles.countDownBadge}
								solveTo={moment(item?.isImmediate?.solveTo)}
								createdAt={getMomentDate(item.createdAt)}
								onTimeRunOut={() => setIsLate(true)}
							/>
						)}
						<UserAvatarImage
							userId={item.patient.id}
							profileImageUrl={item.patient.profileImageUrl}
							isDoctor={false}
							createdAt={item?.patient?.avatarFile?.createdAt}
							customClass={styles.patientImage}
						/>
						{item.isNew && (
							<div className={joinClasses([styles.badge, styles.red])}>
								{t('base.brandNew')}
							</div>
						)}
					</div>
					<div className={styles.patientData}>
						<div className={styles.date}>
							{moment(item.lastCaseEntry?.time).format(
								dateFormatMoment + ', HH:mm',
							)}
						</div>
						<div className={styles.title}>
							<PatientProfileButton
								patient={item.patient}
								workplaceId={workplaceId}
								caseId={item.id}
							/>
						</div>
						<div className={styles.text}>
							<span>
								{item.patient.profile?.age} {t('base.years.many')},{' '}
								{item.patient.profile?.address?.city ||
									item.patient.profile?.address?.region}
							</span>
						</div>
						{identification.value && (
							<div className={styles.text}>
								<span>{t(identification.title)}: </span>
								{identification.value}
							</div>
						)}
						{item.patient.profile?.insuranceCompanyTermItem && (
							<div className={styles.text}>
								<span>
									<Text tx='base.shared.insuranceCompany' inline firstUpper />:
								</span>{' '}
								{item.patient.profile.insuranceCompanyTermItem?.value}
							</div>
						)}
						{item.patientParent && (
							<div className={styles.text}>
								<span>
									<Text
										tx='base.shared.legalRepresentative'
										inline
										firstUpper
									/>
									:
								</span>
								<div>{item.patientParent.profile.fullName}</div>
							</div>
						)}
						{hasPersonActingOnBehalfOfAPatient && (
							<div className={styles.text}>
								<span>
									<Text
										tx='component.patientProfilePersonActingOnBehalfOfAPatient.title'
										inline
										firstUpper
									/>
									:
								</span>
								<div>{getPersonActingOnBehalfOfAPatient()}</div>
							</div>
						)}
						{/*{false && <PatientLanguageBadge user={item.patient as User} />}*/}
						{item.patient.partnerTag?.title && (
							<div className='mt-1'>
								<PatientPartnerBadge partner={item.patient.partnerTag.title} />
							</div>
						)}
						{(item.tags?.length || 0) > 0 &&
							item.tags?.map(caseTag => (
								<div className='mt-1'>
									<CaseTagBadge caseTag={caseTag} />
								</div>
							))}
					</div>
				</div>
				<div className={styles.content}>
					{isLate && (
						<div className={styles.lateText}>
							{t('component.caseListItem.isLate')}
						</div>
					)}
					<div className={styles.title}>{title}</div>
					{item.caseType?.code === 'permanent_medication' &&
						item.medicationLasted &&
						(item.medicationLasted?.length || 0) > 0 && (
							<div className={styles.title}>
								{item.medicationLasted.map((i, index) => {
									const [firstWord, ...rest] = i.value.split(' ');
									return (
										<span key={index}>
											{firstWord}{' '}
											<span className={styles.light}>{rest.join(' ')}</span>
											{index < (item.medicationLasted?.length || 0) - 1 &&
												','}{' '}
										</span>
									);
								})}
							</div>
						)}
					<div>
						{item.lastNote && (
							<CaseNotePreview
								text={item.lastNote.text}
								className={styles.note}
							/>
						)}
						{!isDisabled && (
							<CaseNoteButton
								note={item.lastNote}
								className={styles.noteButton}
								caseId={item.id}
								onSuccess={onRefresh}
								firstUpper
							/>
						)}
					</div>
					{item?.patient?.userRelations?.map(
						({ id, relationUser, type }, index) => {
							if (type && [1, 3, 4, 8].includes(type)) {
								return (
									<div className={styles.contentItem} key={id}>
										<div className={styles.lightGrey}>
											<Text
												tx='base.shared.attendingDoctor'
												inline
												firstUpper
											/>
											:
										</div>
										<div>
											{relationUser?.profile?.fullName ?? (
												<Text tx='base.shared.unassigned' inline firstUpper />
											)}
										</div>
									</div>
								);
							}
						},
					)}
					{item.workplace && showWorkplace && (
						<div className={styles.contentItem}>
							<div className={styles.lightGrey}>
								<Text tx='base.shared.workplace' inline firstUpper />:
							</div>
							<div>{item.workplace.doctorDisplayName}</div>
						</div>
					)}
					{showAssignedDoctor && (
						<div className={styles.contentItem}>
							<div className={styles.lightGrey}>
								<Text tx='base.shared.pickedParamedic' inline firstUpper />:
							</div>
							<div>
								{item.doctor?.profile.fullName || (
									<Text tx='base.shared.unassigned' inline firstUpper />
								)}
							</div>
						</div>
					)}
					{!isDisabled && (
						<CaseForwarder
							caseId={item.id}
							className={styles.caseForwarder}
							onSuccess={onRefresh}
						/>
					)}
					{item.reassignedFrom && (
						<div className={styles.contentItem}>
							<div className={styles.lightGrey}>
								<Text tx='base.shared.caseTransferFrom' inline firstUpper />:
							</div>
							<div>
								{item.reassignedFrom?.user?.profile.fullName} (
								{item.reassignedFrom?.workplace?.doctorDisplayName})
							</div>
						</div>
					)}
					<div className={joinClasses([styles.contentItem, styles.gap30])}>
						{item?.priority?.value && (
							<div className={styles.contentItem}>
								<div className={joinClasses([styles.lightGrey, styles.noGap])}>
									<Text tx='base.priority' inline firstUpper />:
								</div>
								<div className={styles[PRIORITY_COLORS[item.priority.value]]}>
									{t(PRIORITY_TRANSLATIONS[item.priority.value])}
								</div>
							</div>
						)}
						{item?.code && (
							<div className={styles.contentItem}>
								<div className={joinClasses([styles.lightGrey, styles.noGap])}>
									<Text tx='base.numberId' inline firstUpper />:
								</div>
								<div>{item.code}</div>
							</div>
						)}
					</div>
				</div>
				<div className={styles.controls}>
					{isDisabled ? (
						<div className={styles.disabledInfo}>
							<div className={styles.disabledInfoTitle}>
								{t('component.casesSwitch.caseTakenBy')}:
							</div>
							<div className={styles.disabledInfoValue}>
								{item.doctor?.profile.fullName}
							</div>
						</div>
					) : (
						<>
							{item.isNew ? (
								<Button
									title={'base.shared.contactAPatient'}
									theme={Theme.GREEN}
									onClick={() => handleConnectWithPatient(item.id, true)}
								/>
							) : (
								<>
									<Button
										title={'base.shared.continue'}
										theme={Theme.GREEN}
										onClick={() => handleConnectWithPatient(item.id)}
										badge={
											totalUnreadCaseEntries &&
											parseInt(totalUnreadCaseEntries) > 0
												? totalUnreadCaseEntries
												: undefined
										}
									/>
									<CloseCaseButton
										caseId={item.id}
										patientId={item.child?.id ?? item.patient.id}
										onCaseClose={onRefresh}
										caseData={item as Case}
									/>
								</>
							)}
						</>
					)}
					{item.caseChatPreview && (
						<LinkButton
							title='base.messagePreview'
							theme={Theme.BLUE}
							onClick={() => setIsChatPreviewOpen(true)}
							className={styles.chatPreviewButton}
						/>
					)}
					{item.caseType?.code === 'permanent_medication' &&
						item.medicationLasted &&
						(item.medicationLasted?.length || 0) > 0 && (
							<CasePermanentMedicationOptionsContextMenu
								caseId={item.id}
								patientId={item.patient.id}
								medications={item.medicationLasted?.map(
									item => item.medicationId,
								)}
								reloadCaseList={handleReloadCaseList}
							/>
						)}
				</div>
			</div>
			{isChatPreviewOpen && (
				<CaseChatPreviewModal
					caseId={item.id}
					isOpen
					onClose={() => setIsChatPreviewOpen(false)}
				/>
			)}
		</>
	);
};

export default CaseListItem;
